/**
 * DemoPasswordGate — full-screen passphrase prompt shown before the app loads
 * when VITE_DEMO_MODE=true (Acme Law Firm demo sandbox).
 *
 * The passphrase itself lives ONLY on the server (DEMO_PASSPHRASE env var),
 * so rotating it is a single env-var swap + container restart — no frontend
 * rebuild needed. The gate POSTs to /api/demo/unlock (server-side check) and
 * stores the returned token in localStorage; the DemoResetButton reads the
 * same token. Token survives until the container restarts (in-memory set),
 * which is exactly what we want for "rotate weekly": old tokens are
 * invalidated when DEMO_PASSPHRASE changes + container restarts.
 *
 * When VITE_DEMO_MODE != 'true' the gate is transparent — production
 * tenants (S&S, K&K) are unaffected.
 *
 * Ported from cessnamike/valentine-demo (2026-04-17), tightened so the
 * passphrase isn't baked into the JS bundle.
 */
import { useEffect, useState } from 'react';
import api from '../api/client';
import PasswordRevealInput from './PasswordRevealInput';

const TOKEN_STORAGE_KEY = 'demo_unlock_token';
const DEMO_MODE = import.meta.env.VITE_DEMO_MODE === 'true';

function storedToken(): string | null {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
}

interface Props {
  children: React.ReactNode;
}

export default function DemoPasswordGate({ children }: Props) {
  // unlocked: true → render the app; false → show the gate; null → verifying.
  // Non-demo builds are always unlocked. With a stored token we can't trust it
  // alone — the staff_session cookie may have expired/been cleared while the
  // token lingers in localStorage, which would otherwise strand the visitor on
  // a credential-less login. So when a token exists we verify the native
  // session first (effect below) and re-show the gate if it's gone.
  const [unlocked, setUnlocked] = useState<boolean | null>(() => {
    if (!DEMO_MODE) return true;
    return storedToken() ? null : false;
  });

  useEffect(() => {
    if (unlocked !== null) return;
    let cancelled = false;
    fetch('/api/auth/native/session', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled) return;
        if (d?.authenticated) {
          setUnlocked(true);
        } else {
          localStorage.removeItem(TOKEN_STORAGE_KEY);  // stale token → re-unlock
          setUnlocked(false);
        }
      })
      .catch(() => {
        if (!cancelled) setUnlocked(false);  // probe failed → show the gate
      });
    return () => { cancelled = true; };
  }, [unlocked]);
  const [input, setInput] = useState('');
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (unlocked === null) return null;       // brief: verifying the native session
  if (unlocked) return <>{children}</>;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await api.post<{ token: string }>('/demo/unlock', { passphrase: input });
      localStorage.setItem(TOKEN_STORAGE_KEY, res.data.token);
      // /demo/unlock also opens a native staff session and sets the
      // staff_session cookie, so reload to let AuthContext pick it up — the
      // visitor lands straight in the app instead of a second login screen.
      // (If the session wasn't set, the reloaded app simply shows the normal
      // login, i.e. the previous behavior.)
      window.location.assign('/');
    } catch {
      setError(true);
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setInput('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #1a1023 0%, #2d1b3d 40%, #4a2040 70%, #6b2040 100%)',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    }}>
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-6px); }
          80% { transform: translateX(6px); }
        }
        .gate-shake { animation: shake 0.4s ease-in-out; }
      `}</style>
      <div
        className={shake ? 'gate-shake' : ''}
        style={{
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 16,
          padding: '3rem 2.5rem',
          width: '100%',
          maxWidth: 380,
          textAlign: 'center',
        }}
      >
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{
            display: 'inline-block',
            background: 'rgba(244,63,94,0.15)',
            border: '1px solid rgba(244,63,94,0.3)',
            borderRadius: 8,
            padding: '6px 14px',
            fontSize: '0.7rem',
            letterSpacing: '2px',
            textTransform: 'uppercase',
            color: '#f43f5e',
            marginBottom: '1.25rem',
          }}>
            Demo Environment
          </div>
          <h1 style={{
            color: '#fff',
            fontSize: '1.5rem',
            fontWeight: 700,
            margin: '0 0 0.5rem',
          }}>
            Valentine Legal
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.875rem', margin: 0 }}>
            Enter the passphrase to explore the demo
          </p>
        </div>
        <form onSubmit={handleSubmit}>
          <PasswordRevealInput
            value={input}
            onChange={(e) => { setInput(e.target.value); setError(false); }}
            placeholder="Passphrase"
            autoFocus
            toggleColor="rgba(255,255,255,0.5)"
            wrapperStyle={{ marginBottom: '1rem' }}
            inputStyle={{
              width: '100%',
              padding: '0.85rem 1rem',
              borderRadius: 8,
              border: error ? '1px solid #f43f5e' : '1px solid rgba(255,255,255,0.12)',
              background: 'rgba(0,0,0,0.25)',
              color: '#fff',
              fontSize: '0.95rem',
              outline: 'none',
              fontFamily: 'inherit',
              boxSizing: 'border-box',
            }}
          />
          <button
            type="submit"
            disabled={submitting}
            style={{
              width: '100%',
              padding: '0.85rem',
              borderRadius: 8,
              border: 'none',
              background: 'linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)',
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.95rem',
              cursor: submitting ? 'wait' : 'pointer',
              opacity: submitting ? 0.7 : 1,
              fontFamily: 'inherit',
            }}
          >
            {submitting ? 'Checking…' : 'Unlock'}
          </button>
        </form>
      </div>
    </div>
  );
}
